
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthState } from "./useAuthState";
import { signUp, signIn, resetPassword } from "@/utils/auth/authFunctions";
import type { AuthFormData } from "@/utils/auth/validationSchema";
import { toast } from "./use-toast";

export const useAuth = () => {
  const navigate = useNavigate();
  const { user } = useAuthState();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<AuthFormData>({
    email: "",
    password: "",
    first_name: "",
    last_name: "",
  });
  const [authError, setAuthError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setAuthError(null);
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setAuthError(null);
    
    try {
      await signUp(formData);
      toast({
        title: "Success",
        description: "Please check your email to verify your account.",
      });
      navigate("/auth"); // Redirect to login after signup
    } catch (error: any) {
      console.error("Sign up error:", error);
      setAuthError(error.message);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to sign up",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setAuthError(null);
    
    try {
      const { user, isAdmin, role } = await signIn(formData.email, formData.password);
      
      toast({
        title: "Welcome back!",
        description: "You have been signed in successfully.",
      });
      
      // Check if user profile exists and determine roles
      console.log("Login successful, user role:", role, "isAdmin:", isAdmin);
      
      // Navigate based on user role - always check 2FA requirement first 
      // for admin users before redirecting to admin dashboard
      if (isAdmin || role === 'admin') {
        // Redirect to dashboard, the 2FA check will happen in the protected route
        navigate("/admin/dashboard");
      } else if (role === 'instructor') {
        navigate("/admin/dashboard");
      } else {
        navigate("/courses");
      }
    } catch (error: any) {
      console.error("Sign in error:", error);
      setAuthError(error.message);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to sign in",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!formData.email) {
      setAuthError("Please enter your email address to reset your password");
      return;
    }
    
    setIsLoading(true);
    setAuthError(null);

    try {
      await resetPassword(formData.email);
      toast({
        title: "Password reset email sent",
        description: "If your email is registered, you will receive reset instructions.",
      });
    } catch (error: any) {
      console.error("Password reset error:", error);
      setAuthError(error.message);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to request password reset",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    formData,
    authError,
    user,
    handleChange,
    handleSignUp,
    handleSignIn,
    handlePasswordReset,
  };
};
